import {
  AgentSessionManager,
  CallContactEventStatus,
  Permissions,
  LocalStorageHelper,
  StorageKeys,
  ValidationUtils
} from '@nice-ccf/core-sdk';
import { CallContactEvent, MuteEvent } from '@nice-ccf/common-sdk';
import { ControlButtonText } from '../enum/control-button-text';
import { CallControlButton } from '../model/call-control-button';
import { HandleCallback } from '../model/handle-callback';
import { CallContact } from '../model/call-contact';
import { ContactType } from '../enum/contact-type';

/**
 * Class to perform the business logic for show / hide and enable / disable the voice contact buttons
 */
export class VoiceContact {
  private agentSession = <AgentSessionManager>{};
  private callContactEvent = <CallContactEvent>{};
  private callControlButton = <CallControlButton>{};
  private callContact = <CallContact>{};
  private agentMuted = false;
  private isMuteVisible = false;
  private isMaskVisible = false;
  private isRecordVisible = false;
  private validationUtils = new ValidationUtils();
  public handleContact!: HandleCallback;
  public callContacts = [] as Array<CallContactEvent>;

  /**
   * Method to initialize the call contact buttons and subscribe the call contact and mute event handler
   * @param callback - get the callback function from acd base
   * @example
   * ```
   * initialize(callback)
   * ```
   */
  initialize(callback: HandleCallback) {
    this.handleContact = callback;
    this.agentSession = AgentSessionManager.instance;
    this.callContactEventHandler();
    this.muteEventHandler();
    this.initiateButtonStates();
    this.updateAgentPreference();
  }

  /**
   * Method to subscribe the call contact event from the agentSession
   */
  private callContactEventHandler() {
    this.agentSession.callContactEventSubject.subscribe((callContact: CallContactEvent) => {
        this.callContactEvent = callContact;
        this.onVoiceContactUpdate(callContact);
      }
    );
  }

  /**
   * Update CallContacts array once get the CallContactEvent from the get-next
   */
  private onVoiceContactUpdate(callContact: CallContactEvent) {
    let newCall = true;

    if (this.callContacts && this.callContacts.length > 0) {
      this.callContacts.map((voice, i) => {
        if (voice.contactID === callContact.contactID) {
          this.callContacts[i] = callContact;
          newCall = false;
        }
      });
    }

    if (newCall) {
      this.callContacts.push(callContact);
    }
    this.createVoiceContact();
  }

  /**
   * Method to subscribe the mute event from the agentSession
   */
  private muteEventHandler() {
    this.agentSession.muteEventSubject.subscribe((event: MuteEvent) => {
      this.agentMuted = event.agentMuted;
      this.createVoiceContact();
    });
  }

  /**
   * Method to check the agent has mute permission
   * @example
   * ```
   * this.updateAgentPreference()
   * ```
   */
  public updateAgentPreference() {
    this.isMuteVisible = false;
    this.isMaskVisible = false;
    this.isRecordVisible = false;
    const agentPermissions = LocalStorageHelper.getItem(StorageKeys.PERMISSIONS);

    if (agentPermissions) {
      const permissions = JSON.parse(agentPermissions);

      permissions.forEach((permission: Permissions) => {
        switch (permission.key.toLowerCase()) {
          case 'muteagent':
            this.isMuteVisible = true;
            break;
          case 'masking':
            this.isMaskVisible = true;
            break;
          case 'recordcontact':
            this.isRecordVisible = true;
            break;
        }
      });
      this.createVoiceContact();
    }
  }

  /**
   * Method to initiate the button states
   */
  private initiateButtonStates() {
    this.callControlButton = {
      hold: {
        isVisible: true,
        isEnable: false,
        controlText: ControlButtonText.HOLD,
      },
      mute: {
        isVisible: false,
        isEnable: false,
        controlText: ControlButtonText.MUTE,
      },
      end: {
        isVisible: true,
        isEnable: false,
        controlText: ControlButtonText.END,
      },
      mask: {
        isVisible: false,
        isEnable: false,
        controlText: ControlButtonText.MASK,
      },
      record: {
        isVisible: false,
        isEnable: false,
        controlText: ControlButtonText.RECORD,
      },
      transfer: {
        isVisible: true,
        isEnable: false,
        controlText: ControlButtonText.TRANSFER,
      },
    };
  }

  /**
   * Method to create voice contact and send it to callback method
   */
  private createVoiceContact() {
    if (!this.validationUtils.isValidObject(this.callContactEvent)) return;

    if (
      this.callContacts.length === 1 ||
      (this.callContacts.length > 1 &&
        this.callContacts[0].contactID === this.callContactEvent.contactID)
    ) {
      this.updateCallControlButtons();
    } else {
      this.updateMultiCallControlButtons();
    }

    if (
      this.callContactEvent.status === CallContactEventStatus.DISCONNECTED &&
      this.callContactEvent.finalState
    ) {
      this.removeContactFromStore(this.callContactEvent.contactID);
    }

    this.callContact.callControlButton = this.callControlButton;
    this.callContact.contact = this.callContactEvent;
    this.callContact.type = ContactType.VOICE_CONTACT;
    this.handleContact(this.callContact);
  }

  /**
   * remove the contact from callContacts array if that contact status is 'disconnected' with finalstate 'true'
   */
  private removeContactFromStore(contactId: number) {
    this.callContacts.map((voice, i) => {
      if (voice.contactID === contactId) {
        this.callContacts.splice(i, 1);
      }
    });
  }

  /**
   * Method to set default states of the call controls
   */
  private updateCallControlButtonsDefaultState() {
    this.callControlButton.mute.isEnable = true;
    this.callControlButton.mute.controlText = ControlButtonText.MUTE;
    this.callControlButton.mute.isVisible = this.isMuteVisible ? true : false;
    this.callControlButton.mask.isEnable = true;
    this.callControlButton.mask.controlText = ControlButtonText.MASK;
    this.callControlButton.mask.isVisible = this.isMaskVisible ? true : false;
    this.callControlButton.record.isEnable = true;
    this.callControlButton.record.controlText = ControlButtonText.RECORD;
    this.callControlButton.record.isVisible = this.isRecordVisible ? true : false;
    this.callControlButton.hold.isEnable = true;
    this.callControlButton.hold.controlText = ControlButtonText.HOLD;
    this.callControlButton.hold.isVisible = true;
    this.callControlButton.transfer.isEnable = true;
    this.callControlButton.transfer.controlText = ControlButtonText.TRANSFER;
    this.callControlButton.transfer.isVisible = true;
    this.callControlButton.end.isEnable = true;
    this.callControlButton.end.controlText = ControlButtonText.END;
    this.callControlButton.end.isVisible = true;
  }

  /**
   * Method to set state of the call controls whein status is holding
   */
  private updateCallControlButtonsOnHold() {
    this.callControlButton.hold.isEnable = true;
    this.callControlButton.hold.controlText = ControlButtonText.RESUME;
    this.callControlButton.end.isEnable = false;
    this.callControlButton.mask.isEnable = false;
    this.callControlButton.record.isEnable = false;
  }

  /**
   * Method to set state of the call controls on mute
   */
  private updateCallControlButtonsOnMute() {
    this.callControlButton.mute.isVisible = true;
    this.callControlButton.mute.isEnable = true;
    this.callControlButton.mute.controlText = ControlButtonText.UNMUTE;
    this.callControlButton.end.isEnable = false;
  }

  /**
   * Method to set state of the call controls whein status is masking
   */
  private updateCallControlButtonsOnMask() {
    this.callControlButton.mask.isVisible = true;
    this.callControlButton.mask.isEnable = true;
    this.callControlButton.mask.controlText = ControlButtonText.UNMASK;
    this.callControlButton.end.isEnable = false;
    this.callControlButton.hold.isEnable = false;
  }

  /**
   * Method to set state of the Record call control whein isLogging is true
   */
  private updateCallControlButtonsOnRecord() {
    this.callControlButton.record.isVisible = true;
    this.callControlButton.record.isEnable = false;
    this.callControlButton.record.controlText = ControlButtonText.RECORDING;
  }

  /**
   * Method to set state of the Record call control when status is incomming
   */
  private updateCallControlButtonsOnIncoming() {
    this.callControlButton.mute.isEnable = false;
    this.callControlButton.mask.isEnable = false;
    this.callControlButton.record.isEnable = false;
    this.callControlButton.hold.isEnable = false;
    this.callControlButton.transfer.isEnable = false;
    this.callControlButton.end.isEnable = this.callContactEvent.status === CallContactEventStatus.INCOMING ? false : true;
  }

  /**
   * This method to set all call buttons visibility false
   */
  private updateCallControlButtonsOnEnd() {
    this.callControlButton.mute.isVisible = false;
    this.callControlButton.mask.isVisible = false;
    this.callControlButton.record.isVisible = false;
    this.callControlButton.hold.isVisible = false;
    this.callControlButton.end.isVisible = false;
    this.callControlButton.transfer.isVisible = false;
  }

  /**
   * Method to perform the logic for enable / disable the call controls
   */
  private updateCallControlButtons() {
    const status = this.callContactEvent.status;
    const isLogging = this.callContactEvent.isLogging;
    this.initiateButtonStates();

    if (status !== CallContactEventStatus.INCOMING && status !== CallContactEventStatus.DISCONNECTED && status !== CallContactEventStatus.DIALING) {
      this.updateCallControlButtonsDefaultState();
      if (status === CallContactEventStatus.HOLDING) {
        this.updateCallControlButtonsOnHold();
      }

      if (this.agentMuted) {
        this.updateCallControlButtonsOnMute();
      }

      if (status === CallContactEventStatus.MASKING) {
        this.updateCallControlButtonsOnMask();
      }

      if (isLogging) {
        this.updateCallControlButtonsOnRecord();
      }

      if (status === CallContactEventStatus.JOINED) {
        this.callControlButton.hold.isEnable = false;
      }
    } else if (status === CallContactEventStatus.DISCONNECTED) {
      this.updateCallControlButtonsOnEnd();
    } else if (status === CallContactEventStatus.INCOMING) {
      this.updateCallControlButtonsOnIncoming();
    } else if (status === CallContactEventStatus.DIALING) {
      this.updateCallControlButtonsDefaultState();
      this.updateCallControlButtonsOnIncoming();
    }
  }
 
  /**
   * Method to handle the controls for the second call contact
   */
  private updateMultiCallControlButtons() {
    const status = this.callContactEvent.status;
    this.initiateButtonStates();
    this.callControlButton.transfer.isVisible = false;
    switch (status) {
      case CallContactEventStatus.DIALING:
        this.callControlButton.hold.isVisible = false;

        this.callControlButton.end.isVisible = true;
        this.callControlButton.end.isEnable = true;
        break;
      case CallContactEventStatus.ACTIVE:
        this.callControlButton.hold.isVisible = true;
        this.callControlButton.hold.isEnable = true;
        this.callControlButton.hold.controlText = ControlButtonText.HOLD;

        this.callControlButton.end.isVisible = true;
        this.callControlButton.end.isEnable = true;
        break;
      case CallContactEventStatus.HOLDING:
        this.callControlButton.hold.isVisible = true;
        this.callControlButton.hold.isEnable = true;
        this.callControlButton.hold.controlText = ControlButtonText.RESUME;

        this.callControlButton.end.isVisible = true;
        this.callControlButton.end.isEnable = false;
        break;
      case CallContactEventStatus.JOINED:
        this.callControlButton.hold.isVisible = false;

        this.callControlButton.end.isVisible = true;
        this.callControlButton.end.isEnable = true;

        this.callControlButton.transfer.isVisible = true;
        this.callControlButton.transfer.isEnable = true;
        break;
      case CallContactEventStatus.DISCONNECTED:
        this.updateCallControlButtonsOnEnd();
        break;
    }
  }

  /**
   * check the received contactID is present in the call contacts array, if so then assign those contact details 
   * to current contact and create call control buttons for that particular contact.
   * @param contactID - contact id
   * @example
   * ```
   * this.getButtonState(123);
   * ```
   */
   public getButtonState(contactID: number) {
    if (this.callContacts && this.callContacts.length > 0) {      
      this.callContacts.map((voice: CallContactEvent) => {
        if (voice.contactID === contactID) {
          this.callContactEvent = voice;
        }
      });
      this.createVoiceContact();
    }
  }
}
